/**
 * Lobby Screen Component - Generic, parameterized by game type
 */

import { useEffect, useState } from "react";
import { getGameType } from "../../game-registry";
import {
  buildGamePath,
  buildGameRoomPath,
  navigateTo,
  type RouteMode,
} from "../../router";
import { Alert, Button } from "../../ui";
import { useLobbyWS } from "../hooks/use-lobby-ws";
import { getUserLabel, useAuth, useAuthActions } from "../store/auth-store";
import { useLobby, useLobbyActions } from "../store/lobby-store";
import { getParticipantDisplayName } from "../utils/participants";
import { deriveRoomDisplayName } from "../utils/room-display";

interface LobbyScreenProps {
  readonly gameType: string;
  readonly routeMode?: RouteMode;
}

export function LobbyScreen({ gameType, routeMode = "v2" }: LobbyScreenProps) {
  const { user } = useAuth();
  const authActions = useAuthActions();
  const { games, myGames, isLoading, error } = useLobby();
  const lobbyActions = useLobbyActions();
  const gameConfig = getGameType(gameType);

  const [createConfig, setCreateConfig] = useState<Record<string, unknown>>({});
  const [observerMode, setObserverMode] = useState(false);
  const [observerSeatCount, setObserverSeatCount] = useState(2);

  const CreateGameForm = gameConfig?.CreateGameForm;

  useEffect(() => {
    lobbyActions.setGameType(gameType, routeMode);
  }, [gameType, routeMode, lobbyActions.setGameType]);

  // Real-time lobby updates via WebSocket (with polling fallback)
  useLobbyWS(gameType, lobbyActions, routeMode);

  // Surface rejoinable games on mount and whenever auth changes.
  useEffect(() => {
    if (!user) return;
    void lobbyActions.fetchMyGames();
  }, [user, lobbyActions.fetchMyGames]);

  const rejoinGames = myGames.filter(
    (g) =>
      g.gameType === gameType && g.participantUserIds?.includes(user?.id ?? ""),
  );

  const handleCreateGame = async () => {
    // Forward the registry's player-count config so the backend's
    // default-of-2 cap doesn't reject the 3rd+ joiner in games like horror-race.
    const config: Record<string, unknown> = { ...createConfig };
    if (gameConfig?.maxPlayers !== undefined) {
      config.maxPlayers = gameConfig.maxPlayers;
    }
    if (gameConfig?.minPlayers !== undefined) {
      config.minPlayers = gameConfig.minPlayers;
    }
    if (gameConfig?.supportsObservers) {
      config.observerMode = observerMode;
      config.observerSeatCount = observerMode ? observerSeatCount : 0;
    }
    const game = await lobbyActions.createGame(config);
    if (game) {
      navigateTo(buildGameRoomPath(gameType, game.id, routeMode));
    }
  };

  const handleJoinGame = async (gameId: string) => {
    const game = await lobbyActions.joinGame(gameId);
    if (game) {
      navigateTo(buildGameRoomPath(gameType, game.id, routeMode));
    }
  };

  const handleJoinObserver = async (gameId: string) => {
    const game = await lobbyActions.joinObserver(gameId);
    if (game) {
      navigateTo(buildGamePath(gameType, game.id, routeMode));
    }
  };

  const handleConfigChange = (config: Record<string, unknown>) => {
    setCreateConfig(config);
  };

  const displayName = gameConfig?.displayName ?? gameType;
  const maxPlayers = gameConfig?.maxPlayers ?? 2;

  return (
    <div className="flex min-h-screen flex-col items-center p-3 sm:p-6">
      {/* Header */}
      <div className="mb-4 flex w-full max-w-2xl items-center justify-between sm:mb-6">
        <div>
          <h1 className="m-0 font-bold text-lg sm:text-xl">
            {displayName} 로비
          </h1>
          <p className="mt-1 text-text-muted text-xs">
            대기 중인 방에 참여하거나 새 방을 만드세요
          </p>
        </div>
        <div className="flex items-center gap-2">
          {user && (
            <span
              className="text-text-muted text-xs"
              data-testid="lobby-user-label"
            >
              {getUserLabel(user)}
            </span>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigateTo("/")}
            className="text-xs"
          >
            ‹ 뒤로
          </Button>
          <Button
            variant="ghost"
            size="sm"
            data-testid="logout-btn"
            onClick={() => void authActions.logout()}
            className="text-xs"
          >
            로그아웃
          </Button>
        </div>
      </div>

      {error && <Alert className="mb-4 w-full max-w-2xl">{error}</Alert>}

      {/* Create Game */}
      <div className="mb-4 w-full max-w-2xl sm:mb-6">
        {CreateGameForm && (
          <div className="mb-3">
            <CreateGameForm onConfigChange={handleConfigChange} />
          </div>
        )}
        {gameConfig?.supportsObservers && (
          <div className="mb-3 rounded-md border border-border-subtle bg-surface-secondary p-3">
            <label className="flex items-start gap-3 text-sm">
              <input
                type="checkbox"
                data-testid="observer-mode-toggle"
                checked={observerMode}
                onChange={(event) => setObserverMode(event.target.checked)}
                className="mt-1"
              />
              <span className="flex-1">
                <span className="block font-semibold text-text-primary">
                  관전 모드 열기
                </span>
                <span className="mt-1 block text-text-muted text-xs">
                  관전자는 플레이어 자리를 차지하지 않고 보드만 볼 수 있습니다.
                </span>
              </span>
            </label>
            {observerMode && (
              <label className="mt-3 flex items-center justify-between gap-3 text-sm">
                <span className="font-semibold text-text-secondary">
                  관전석 수
                </span>
                <input
                  type="number"
                  min={1}
                  max={12}
                  data-testid="observer-seat-count-input"
                  value={observerSeatCount}
                  onChange={(event) =>
                    setObserverSeatCount(
                      Math.min(
                        12,
                        Math.max(1, Number(event.target.value) || 1),
                      ),
                    )
                  }
                  className="w-20 rounded-md border border-border-primary bg-surface-primary px-2 py-1 text-right text-text-primary"
                />
              </label>
            )}
          </div>
        )}
        <Button
          data-testid="create-game-btn"
          onClick={handleCreateGame}
          disabled={isLoading}
          className="w-full"
        >
          + 새 게임 만들기
        </Button>
      </div>

      {/* Rejoin: rooms the user is already a participant in */}
      {rejoinGames.length > 0 && (
        <div
          className="mb-4 w-full max-w-2xl sm:mb-6"
          data-testid="rejoin-section"
        >
          <div className="mb-3 font-semibold text-sm text-text-secondary">
            진행 중인 내 게임
          </div>
          <div className="flex flex-col gap-2">
            {rejoinGames.map((game) => (
              <div
                key={game.id}
                data-testid="rejoin-item"
                className="flex items-center justify-between rounded-md border border-border-subtle bg-surface-secondary px-4 py-3"
              >
                <div>
                  <div className="font-semibold text-sm">
                    {deriveRoomDisplayName(game.id)}
                  </div>
                  <div className="mt-1 text-text-muted text-xs">
                    상태:{" "}
                    {game.status === "IN_PROGRESS" ? "진행 중" : "대기 중"}
                  </div>
                </div>
                <Button
                  variant="primary"
                  size="sm"
                  data-testid="rejoin-game-btn"
                  onClick={() =>
                    navigateTo(buildGameRoomPath(gameType, game.id, routeMode))
                  }
                >
                  다시 입장
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Available Games */}
      <div className="w-full max-w-2xl">
        <div className="mb-3 font-semibold text-sm text-text-secondary">
          대기 중인 방
        </div>

        {isLoading && games.length === 0 ? (
          <p className="text-sm text-text-secondary">
            게임 목록 불러오는 중...
          </p>
        ) : games.length === 0 ? (
          <p className="text-sm text-text-secondary">
            참여 가능한 게임이 없습니다. 새 게임을 만들어보세요!
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {games.map((game) => {
              const isFull = game.currentPlayerCount >= maxPlayers;
              const isOwn = game.hostUserId === user?.id;
              const canObserve =
                gameConfig?.supportsObservers === true &&
                game.observerMode &&
                (game.currentObserverCount ?? 0) <
                  (game.observerSeatCount ?? 0);
              return (
                <div
                  key={game.id}
                  data-testid="game-item"
                  className="flex items-center justify-between rounded-md border border-border-subtle bg-surface-secondary px-4 py-3"
                >
                  <div>
                    <div className="font-semibold text-sm">
                      {deriveRoomDisplayName(game.id)}
                    </div>
                    <div className="mt-1 text-text-muted text-xs">
                      호스트:{" "}
                      {getParticipantDisplayName(
                        game,
                        game.hostUserId,
                        "알 수 없음",
                      )}{" "}
                      · {game.currentPlayerCount}/{maxPlayers}명
                      {game.observerMode
                        ? ` · 관전자 ${game.currentObserverCount}/${game.observerSeatCount}`
                        : ""}
                    </div>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    {isFull && !isOwn ? (
                      <span className="rounded bg-surface-tertiary px-3 py-1.5 font-semibold text-text-muted text-xs">
                        만석
                      </span>
                    ) : (
                      <Button
                        variant={isOwn ? "primary" : "success"}
                        size="sm"
                        data-testid="join-game-btn"
                        onClick={() => handleJoinGame(game.id)}
                        disabled={isLoading}
                      >
                        {isOwn ? "입장" : "참여"}
                      </Button>
                    )}
                    {canObserve && !isOwn && (
                      <Button
                        variant="secondary"
                        size="sm"
                        data-testid="join-observer-btn"
                        onClick={() => handleJoinObserver(game.id)}
                        disabled={isLoading}
                      >
                        관전
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
